import { Box, LinearProgress, Typography } from "@mui/material";
import {
  DataGridPro,
  GridFooterContainer,
  GridPagination,
} from "@mui/x-data-grid-pro";

import themeInarix from "../../StylesMuiInarix";
import { ThemeProvider } from "@mui/material/styles";
import TableDashboardColumns from "./FetchingColumns";
import { useSelector } from "react-redux";
import { DashboardSelectors } from "../../../redux/selectors/unifiedSelectors";
import { dashboardTableStyles } from "./DashboardTableStyles";

export function DashboardTable(t: (arg0: string) => string, locale: string) {
  const { columnsValues, dashResults } = TableDashboardColumns(t);
  const { loadingProgress, totalActions } = useSelector(DashboardSelectors);

  const paginationToolbar = () => {
    return (
      <GridFooterContainer sx={dashboardTableStyles.paginationToolbar}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("Dashboard.Table.TotalRecords") || "Total:"}{" "}
            {totalActions.toLocaleString()}
          </Typography>
          <GridPagination labelRowsPerPage={t("Dashboard.Users_per_page")} />
        </Box>
      </GridFooterContainer>
    );
  };

  const CustomLoadingOverlay = () => {
    const isComplete = loadingProgress.percentage === 100;

    return (
      <Box sx={dashboardTableStyles.loadingOverlayContainer}>
        <Box sx={dashboardTableStyles.spinnerContainer}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            xmlns="http://www.w3.org/2000/svg"
            stroke="#1976d2"
          >
            <g fill="none" fillRule="evenodd">
              <g transform="translate(1 1)" strokeWidth="2">
                <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
                <path d="M36 18c0-9.94-8.06-18-18-18">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 18 18"
                    to="360 18 18"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </path>
              </g>
            </g>
          </svg>
        </Box>
        <Box sx={dashboardTableStyles.loadingText}>
          {t("Dashboard.Table.Loading")}
        </Box>

        {/* Loading progress bar */}
        {loadingProgress.isLoading && (
          <Box sx={dashboardTableStyles.progressBarContainer}>
            {loadingProgress.totalRecords > 0 ? (
              <>
                <Box sx={dashboardTableStyles.progressInfoContainer}>
                  <Typography
                    variant="body2"
                    sx={{
                      ...dashboardTableStyles.getLoadingTextColor(isComplete),
                      ...dashboardTableStyles.getLoadingTextWeight(isComplete),
                    }}
                  >
                    {isComplete
                      ? `✓ ${loadingProgress.totalRecords.toLocaleString()} ${t("Dashboard.Table.LoadingComplete")}`
                      : `${t("Dashboard.Table.LoadingRecords")} ${loadingProgress.loadedRecords.toLocaleString()} ${t("Dashboard.Table.Of")} ${loadingProgress.totalRecords.toLocaleString()}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      ...dashboardTableStyles.getLoadingTextColor(isComplete),
                      ...dashboardTableStyles.percentageText,
                    }}
                  >
                    {loadingProgress.percentage}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={loadingProgress.percentage}
                  sx={dashboardTableStyles.getLinearProgressStyles(isComplete)}
                />
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={dashboardTableStyles.preparingDataText}
                >
                  {t("Dashboard.Table.PreparingData")}
                </Typography>
                <LinearProgress
                  sx={dashboardTableStyles.indeterminateProgressBar}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={dashboardTableStyles.container}>
      <ThemeProvider theme={themeInarix(locale)}>
        <DataGridPro
          rows={dashResults.length > 0 ? dashResults : []}
          columns={columnsValues}
          pageSizeOptions={[100]}
          loading={loadingProgress.isLoading}
          slots={{
            footer: paginationToolbar,
            loadingOverlay: CustomLoadingOverlay,
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          rowHeight={23.5}
          sx={dashboardTableStyles.dataGrid}
        />
      </ThemeProvider>
    </Box>
  );
}
